import { FaFacebookF, FaInstagram } from 'react-icons/fa6';
import { useEffect, useState } from 'react';

type Shot = {
  id: string;
  title: string;
  label: string;
  src: string;
  alt: string;
};

type Feature = {
  id: string;
  nav: string;
  kicker: string;
  title: string;
  text: string;
  shot: Shot;
};

type TourGroup = {
  id: string;
  kicker: string;
  title: string;
  text: string;
  shots: Shot[];
};

type AppPartner = {
  id: string;
  name: string;
  href: string;
  logoLabel: string;
  logoSrc?: string;
  logoTone?: 'dark';
};

type AppPartnerGroup = {
  id: string;
  label: string;
  partners: AppPartner[];
};

type LegalPageId =
  | 'terms'
  | 'privacy'
  | 'safety'
  | 'community'
  | 'cookies'
  | 'account-delete';

type LegalPageContent = {
  title: string;
  lastUpdated: string;
  intro?: string[];
  clauses: LegalClause[];
};

type LegalClause = {
  number: string;
  title: string;
  paragraphs?: string[];
  items?: string[];
  table?: LegalTable;
  actions?: Array<{ label: string; href: string }>;
};

type LegalTable = {
  headers: string[];
  rows: string[][];
};

type AppProps = {
  origin?: string;
  pathname?: string;
  search?: string;
};

export type PageSeo = {
  canonicalUrl: string;
  description: string;
  dateModified?: string;
  datePublished?: string;
  keywords?: string;
  ogDescription?: string;
  robots?: string;
  schemaGraph: unknown[];
  title: string;
  twitterTitle?: string;
};

export const siteBaseUrl = 'https://na-vodu.cz';
const homePublishedDate = '2026-05-18';
const homeModifiedDate = '2026-06-01';
const legalModifiedDate = '2026-05-21';
const appStoreUrl = 'https://apps.apple.com/cz/app/na-vodu/id6769399296?l=cs';
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=cz.navodu.app';
const facebookUrl = 'https://www.facebook.com/profile.php?id=61589619455528';
const instagramUrl = 'https://www.instagram.com/na_vodu/';
const socialLinks = [
  {
    id: 'instagram',
    label: 'Instagram',
    href: instagramUrl,
    handle: '@na_vodu',
  },
  {
    id: 'facebook',
    label: 'Facebook',
    href: facebookUrl,
    handle: 'Na Vodu',
  },
] as const;

const appPartnerGroups: AppPartnerGroup[] = [
  {
    id: 'restaurants',
    label: 'Restaurace',
    partners: [
      {
        id: 'u-dwau-maryi',
        name: 'Krčma U dwau Maryí',
        href: 'https://www.2marie.cz/',
        logoLabel: 'U dwau Maryí',
        logoSrc: '/assets/partners/u-dwau-maryi-logo.gif',
      },
      {
        id: 'hostinec-u-dusku',
        name: 'Hostinec U Dušků',
        href: 'https://www.facebook.com/hostinecudusku',
        logoLabel: 'U Dušků',
        logoSrc: '/assets/partners/hostinec-u-dusku-logo.jpg',
      },
      {
        id: 'hamr-divci-kamen',
        name: 'Hamr Dívčí Kámen',
        href: 'https://www.hamrdivcikamen.cz/',
        logoLabel: 'Hamr Dívčí Kámen',
        logoSrc: '/assets/partners/hamr-divci-kamen-logo.png',
        logoTone: 'dark',
      },
    ],
  },
  {
    id: 'camps',
    label: 'Kempy',
    partners: [
      {
        id: 'kemp-nove-spoli',
        name: 'Kemp Český Krumlov Nové Spolí',
        href: 'https://www.kempkrumlov.cz/',
        logoLabel: 'Kemp Nové Spolí',
        logoSrc: '/assets/partners/kemp-nove-spoli-logo.png',
      },
      {
        id: 'u-tri-borovic',
        name: 'Kemp U Tří Borovic',
        href: 'https://www.utriborovic.cz/',
        logoLabel: 'U Tří Borovic',
        logoSrc: '/assets/partners/u-tri-borovic-logo.png',
      },
    ],
  },
];

type SocialPlatform = (typeof socialLinks)[number]['id'];

export const prerenderPaths = [
  '/',
  '/stahnout/',
  '/legal/privacy/',
  '/legal/terms/',
  '/legal/safety/',
  '/legal/community/',
  '/legal/cookies/',
  '/account/delete/',
] as const;

const screenShots = {
  plannerCreate: {
    id: 'planner-create',
    title: 'Nová jízda',
    label: 'plán',
    src: '/assets/screens/app/planner-create.jpg',
    alt: 'Formulář nové jízdy s názvem, náladou posádky, řekou a trasou',
  },
  plannerItinerary: {
    id: 'planner-itinerary',
    title: 'Itinerář',
    label: 'dny',
    src: '/assets/screens/app/planner-itinerary.jpg',
    alt: 'Plánovač s plavidly, počtem dní a denním itinerářem',
  },
  rideReady: {
    id: 'ride-ready',
    title: 'Připravená jízda',
    label: 'start',
    src: '/assets/screens/app/ride-ready.jpg',
    alt: 'Připravená jízda Vltava 2026 s posádkou a tlačítkem Zahájit jízdu',
  },
  crewQrInvite: {
    id: 'crew-qr-invite',
    title: 'QR pozvánka',
    label: 'posádka',
    src: '/assets/screens/app/crew-qr-invite.jpg',
    alt: 'QR pozvánka do jízdy pro připojení člena posádky',
  },
  crewMembers: {
    id: 'crew-members',
    title: 'Lidé v posádce',
    label: 'členové',
    src: '/assets/screens/app/crew-members.jpg',
    alt: 'Seznam členů posádky se stavem účasti a čekajícími pozvánkami',
  },
  itineraryOverview: {
    id: 'itinerary-overview',
    title: 'Plán cesty',
    label: 'trasa',
    src: '/assets/screens/app/itinerary-overview.jpg',
    alt: 'Rozbalený plán cesty rozdělený na tři dny',
  },
  itineraryDays: {
    id: 'itinerary-days',
    title: 'Denní úseky',
    label: 'kilometry',
    src: '/assets/screens/app/itinerary-days.jpg',
    alt: 'Denní úseky itineráře s délkou a odhadovaným časem plavby',
  },
  itineraryDayDetail: {
    id: 'itinerary-day-detail',
    title: 'Body dne',
    label: 'zastávky',
    src: '/assets/screens/app/itinerary-day-detail.jpg',
    alt: 'Detail dne s jezy, kempy, restauracemi a tlačítkem zobrazit v mapě',
  },
  radarMap: {
    id: 'radar-map',
    title: 'Říční radar',
    label: 'mapa',
    src: '/assets/screens/app/radar-map.jpg',
    alt: 'Mapa Vltavy s trasou, body na řece a ovládáním radaru',
  },
  poiReview: {
    id: 'poi-review',
    title: 'Místo na řece',
    label: 'recenze',
    src: '/assets/screens/app/poi-review.jpg',
    alt: 'Detail restaurace Hotel U Martina s hodnocením a vlastní recenzí',
  },
  radarRouteLayers: {
    id: 'radar-route-layers',
    title: 'Úseky v mapě',
    label: 'trasa',
    src: '/assets/screens/app/radar-route-layers.jpg',
    alt: 'Výběr úseků itineráře zobrazovaných v mapě',
  },
  eventReport: {
    id: 'event-report',
    title: 'Hlášení',
    label: 'událost',
    src: '/assets/screens/app/event-report.jpg',
    alt: 'Formulář pro nahlášení události na mapě s typem a vážností',
  },
  mapFilters: {
    id: 'map-filters',
    title: 'Vrstvy mapy',
    label: 'filtry',
    src: '/assets/screens/app/map-filters.jpg',
    alt: 'Filtry vrstev mapy pro úkoly, nabídky, události a místa na řece',
  },
  tasksList: {
    id: 'tasks-list',
    title: 'Úkoly posádky',
    label: 'body',
    src: '/assets/screens/app/tasks-list.jpg',
    alt: 'Seznam úkolů posádky s body, vzdáleností a tlačítkem splnit',
  },
  crewChat: {
    id: 'crew-chat',
    title: 'Chat posádky',
    label: 'zprávy',
    src: '/assets/screens/app/crew-chat.jpg',
    alt: 'Chat vlastní posádky se zprávou od kapitána',
  },
  storiesFeed: {
    id: 'stories-feed',
    title: 'Příběhy',
    label: 'okolí',
    src: '/assets/screens/app/stories-feed.jpg',
    alt: 'Příběh posádky v okolí s fotkou, textem a tlačítkem napsat posádce',
  },
  otherCrewChat: {
    id: 'other-crew-chat',
    title: 'Cizí posádka',
    label: 'kontakt',
    src: '/assets/screens/app/other-crew-chat.jpg',
    alt: 'Chat s jinou posádkou dostupnou v aplikaci',
  },
  chatList: {
    id: 'chat-list',
    title: 'Chaty',
    label: 'domluva',
    src: '/assets/screens/app/chat-list.jpg',
    alt: 'Seznam chatů pro vlastní posádku i další posádky na vodě',
  },
  profile: {
    id: 'profile',
    title: 'Profil',
    label: 'účet',
    src: '/assets/screens/app/profile.jpg',
    alt: 'Profil s úpravou profilu, jízdami, offline mapami, blokací a právními informacemi',
  },
  offlineMaps: {
    id: 'offline-maps',
    title: 'Offline mapy',
    label: 'signál',
    src: '/assets/screens/app/offline-maps.jpg',
    alt: 'Offline mapový balíček Vltava s tlačítkem stáhnout',
  },
  profileDark: {
    id: 'profile-dark',
    title: 'Profil v noci',
    label: 'tmavý režim',
    src: '/assets/screens/app/profile-dark.jpg',
    alt: 'Profil aplikace v tmavém režimu',
  },
  chatListDark: {
    id: 'chat-list-dark',
    title: 'Chaty v noci',
    label: 'tmavý režim',
    src: '/assets/screens/app/chat-list-dark.jpg',
    alt: 'Seznam chatů v tmavém režimu',
  },
  storiesDark: {
    id: 'stories-dark',
    title: 'Příběhy v noci',
    label: 'tmavý režim',
    src: '/assets/screens/app/stories-dark.jpg',
    alt: 'Příběhy v tmavém režimu s kartou posádky',
  },
  rideActiveDark: {
    id: 'ride-active-dark',
    title: 'Aktivní jízda',
    label: 'tmavý režim',
    src: '/assets/screens/app/ride-active-dark.jpg',
    alt: 'Aktivní jízda v tmavém režimu s viditelností na mapě a plánem cesty',
  },
  conditionsDark: {
    id: 'conditions-dark',
    title: 'Stav řeky',
    label: 'počasí',
    src: '/assets/screens/app/conditions-dark.jpg',
    alt: 'Karta stavu řeky a počasí v tmavém režimu s teplotním a srážkovým grafem',
  },
  conditionsLight: {
    id: 'conditions-light',
    title: 'Počasí',
    label: 'podmínky',
    src: '/assets/screens/app/conditions-light.jpg',
    alt: 'Karta stavu řeky a počasí se souhrnem vodočtu, větru a srážek',
  },
  crewChatReply: {
    id: 'crew-chat-reply',
    title: 'Odpověď v chatu',
    label: 'zprávy',
    src: '/assets/screens/app/crew-chat-reply.jpg',
    alt: 'Chat s příchozí i odchozí zprávou mezi posádkami',
  },
  expenses: {
    id: 'expenses',
    title: 'Výdaje',
    label: 'peníze',
    src: '/assets/screens/expenses.jpg',
    alt: 'Výdaje posádky v aplikaci Na Vodu',
  },
} satisfies Record<string, Shot>;

const featureItems: Feature[] = [
  {
    id: 'plan',
    nav: 'Naplánovat jízdu',
    kicker: 'trasa',
    title: 'Naplánovat jízdu',
    text: 'Naplánuj si jízdu, zvol počet dní, přidej jako kapitán kamarády do posádky, nastav profilovku posádky, vyber plavidlo i náladu: pohoda, rodina, flirt nebo zábava. Pak si rozlož trasu podle dní a můžeš vyrazit.',
    shot: screenShots.plannerCreate,
  },
  {
    id: 'rewards',
    nav: 'Sbírat odměny',
    kicker: 'body',
    title: 'Sbírat odměny',
    text: 'Po cestě můžeš plnit sezónní úkoly, sbírat body a v aplikaci uvidíš dostupné nabídky nebo vouchery, pokud jsou pro danou sezónu zapnuté.',
    shot: screenShots.tasksList,
  },
  {
    id: 'location',
    nav: 'Sdílet polohu',
    kicker: 'radar',
    title: 'Sdílet polohu s ostatními vodáky',
    text: 'Sdílej polohu s ostatními vodáky. Když chceš, ukážeš se ostatním posádkám na mapě a můžete se navzájem kontaktovat.',
    shot: screenShots.radarMap,
  },
  {
    id: 'messages',
    nav: 'Posílat zprávy',
    kicker: 'domluva',
    title: 'Posílat zprávy',
    text: 'Piš si s vlastní posádkou nebo napiš posádkám dostupným v aplikaci a domluvte se na další zastávce či společné plavbě.',
    shot: screenShots.chatList,
  },
  {
    id: 'stories',
    nav: 'Přidávat příběhy',
    kicker: 'na řece',
    title: 'Přidávat příběhy',
    text: 'Přidávej příběhy, když chceš, aby si tě ostatní všimli. Hledáš na večer zpěváky k táboráku, hraješ pěkně, ale zpívání ti moc nejde? Přidej příběh. Zobrazí se posádkám v okolí a třeba ti přes appku napíšou, nebo večer přijdou k ohni vzadu vpravo v rohu kempu.',
    shot: screenShots.storiesFeed,
  },
  {
    id: 'expenses',
    nav: 'Spočítat výdaje',
    kicker: 'peníze',
    title: 'Spočítat výdaje',
    text: 'Taky tě štve rozpočítávání, kdo komu na konci vody dluží? Výdaje po cestě zapisuješ do kalkulačky a nakonec se ti jen zobrazí, kdo má komu kolik dát.',
    shot: screenShots.expenses,
  },
  {
    id: 'ratings',
    nav: 'Hodnotit podniky',
    kicker: 'místa',
    title: 'Hodnotit podniky',
    text: 'Hodnoť kempy a restaurace. Dej ostatním vědět, kde stálo za to zastavit, kde dobře vaří a kam se při další plavbě rád vrátíš.',
    shot: screenShots.poiReview,
  },
  {
    id: 'offline',
    nav: 'Offline mapy',
    kicker: 'bez signálu',
    title: 'Offline mapy',
    text: 'Okolí Vltavy lze předem stáhnout a mapy tak fungují i bez připojení k internetu.',
    shot: screenShots.offlineMaps,
  },
];

const productTourGroups: TourGroup[] = [
  {
    id: 'planning',
    kicker: 'před vyplutím',
    title: 'Nejdřív se založí jízda.',
    text: 'Kapitán založí jízdu, vybere profilovou fotku, nastaví počet dní, typ plavidla a naplánuje trasu. Aplikace umí navrhnout rozložení jízdy podle zadaného počtu dní a kapitán si ho upraví podle skutečných plánů. Nakonec pozve členy posádky podle uživatelského jména nebo přes QR kód.',
    shots: [
      screenShots.plannerCreate,
      screenShots.plannerItinerary,
      screenShots.rideReady,
      screenShots.crewQrInvite,
      screenShots.crewMembers,
    ],
  },
  {
    id: 'route',
    kicker: 'trasa',
    title: 'Součástí aplikace je itinerář.',
    text: 'V itineráři se můžeš podívat na konkrétní úsek dne, sledovat počet jezů, kempů a restaurací a všechno si rovnou zobrazit na mapě.',
    shots: [
      screenShots.itineraryOverview,
      screenShots.itineraryDays,
      screenShots.itineraryDayDetail,
      screenShots.radarMap,
      screenShots.radarRouteLayers,
      screenShots.mapFilters,
    ],
  },
  {
    id: 'river',
    kicker: 'na řece',
    title: 'Součástí aplikace je i mapa.',
    text: 'V mapě vidíš kempy, restaurace, podniky, úkoly, nabídky a další věci kolem řeky. Všechno si můžeš přehledně filtrovat a vybraným místům udělovat hodnocení. Na mapě můžeš dočasně založit upozornění pro ostatní, třeba na spadlý kmen, nebo jen oznámit, že tady je teď párty. Součástí aplikace je i přehled počasí a stav řeky.',
    shots: [
      screenShots.poiReview,
      screenShots.eventReport,
      screenShots.tasksList,
      screenShots.conditionsLight,
      screenShots.conditionsDark,
    ],
  },
  {
    id: 'crew',
    kicker: 'parta',
    title: 'Všechno se točí kolem jízdy.',
    text: 'V jízdě plníš úkoly, za které získáváš body. Ty potom využiješ k uplatnění slev u partnerů. Piš zprávy své posádce i dalším posádkám, nech si výdajovníkem spočítat, kdo komu kolik dluží, přidej za posádku příběh a kontaktuj další vodáky.',
    shots: [
      screenShots.crewChat,
      screenShots.storiesFeed,
      screenShots.otherCrewChat,
      screenShots.chatList,
      screenShots.crewChatReply,
    ],
  },
  {
    id: 'control',
    kicker: 'kontrola',
    title: 'Profil, offline mapa a tmavý režim.',
    text: 'V profilu si upravíš svůj účet, podíváš se na předešlé absolvované jízdy a stáhneš si offline mapu Vltavy. Aplikace umí i tmavý režim. Na iPhonu ji stáhneš z App Storu.',
    shots: [
      screenShots.profile,
      screenShots.offlineMaps,
      screenShots.profileDark,
      screenShots.chatListDark,
      screenShots.storiesDark,
      screenShots.rideActiveDark,
    ],
  },
];

const legalLastUpdated = '21. května 2026';

const operatorText =
  'Provozovatelem služby Na Vodu je Bc. Martin Anděl, IČO 23998059, sad Míru 788, 285 22 Zruč nad Sázavou. Kontaktní e-mail pro právní, bezpečnostní a produktové dotazy je info@na-vodu.cz. Web provozovatele je https://martinandel.cz.';

const dataControllerText =
  'Správcem osobních údajů je Bc. Martin Anděl, IČO 23998059, sad Míru 788, 285 22 Zruč nad Sázavou. Kontakt pro soukromí a výkon práv subjektů údajů je info@na-vodu.cz. Web provozovatele je https://martinandel.cz.';

const legalPages: Record<LegalPageId, LegalPageContent> = {
  terms: {
    title: 'Podmínky používání aplikace Na Vodu',
    lastUpdated: legalLastUpdated,
    intro: [
      'Tyto podmínky upravují používání mobilní aplikace, webu a navazujících služeb Na Vodu.',
      'Aplikace je komunitní vodácký pomocník. Nenahrazuje vodácký výcvik, oficiální hydrologická data, pokyny správců toku, záchranné složky ani vlastní úsudek posádky.',
    ],
    clauses: [
      {
        number: '1',
        title: 'Provozovatel',
        paragraphs: [
          operatorText,
          'Smluvní vztah vzniká mezi provozovatelem a osobou, která službu používá nebo si v ní vytvoří účet.',
        ],
      },
      {
        number: '2',
        title: 'Co služba poskytuje',
        paragraphs: [
          'Na Vodu pomáhá plánovat jízdy, spravovat posádku, zobrazovat mapu řek, sdílet polohu kapitána, komunikovat v rámci jízdy, ukládat komunitní příběhy, úkoly, události, zajímavá místa, volitelná hlasová upozornění na jezy a související vodácké informace.',
          'Část obsahu pochází od uživatelů, partnerů, veřejných zdrojů nebo z lokálních datových balíčků. Data se mohou opozdit, být neúplná nebo chybná.',
        ],
      },
      {
        number: '3',
        title: 'Kdo může službu používat',
        paragraphs: [
          'Službu smí používat osoba, která rozumí těmto podmínkám a je schopná nést odpovědnost za své chování na vodě. Nezletilí mohou službu používat pouze se souhlasem a dohledem zákonného zástupce; dětem mladším 15 let není určena bez odpovídajícího právního důvodu nebo souhlasu.',
          'Provozovatel může omezit nebo zrušit účet, pokud službu používáte způsobem, který porušuje právo, ohrožuje ostatní nebo narušuje provoz aplikace.',
        ],
      },
      {
        number: '4',
        title: 'Účet a přihlášení',
        paragraphs: [
          'Některé funkce vyžadují účet. Přihlášení může probíhat přes podporované poskytovatele identity nebo jiný mechanismus zapnutý v aplikaci.',
          'Za správnost údajů v profilu a za bezpečné používání svého zařízení odpovídáte vy. Pokud zjistíte zneužití účtu, kontaktujte nás na info@na-vodu.cz.',
        ],
      },
      {
        number: '5',
        title: 'Jízdy, posádky a pozvánky',
        paragraphs: [
          'Jízda je organizační záznam pro skupinu vodáků. Autor jízdy odpovídá za nastavení posádky, pozvánky, viditelnost a obsah jízdy.',
          'Přijetím pozvánky se můžete stát členem posádky a získat přístup k informacím, zprávám a obsahu dané jízdy. Některé údaje mohou zůstat v historii jízdy i po vašem odchodu, pokud je to nutné pro zachování kontextu nebo splnění právních povinností.',
        ],
      },
      {
        number: '6',
        title: 'Poloha a sdílení na mapě',
        paragraphs: [
          'Aplikace může po vašem souhlasu pracovat s přesnou polohou zařízení. Poloha se používá zejména pro zobrazení na mapě, polohové úkoly, vyhledání okolních míst a dobrovolné sdílení polohy kapitána v rámci aktivní jízdy.',
          'Sdílení polohy na mapě je volitelné. Nové jízdy nejsou veřejně viditelné na mapě, dokud takové sdílení vědomě nezapnete.',
          'Pokud v systému zařízení povolíte polohu na pozadí, aplikace ji může používat i při zamčeném telefonu nebo při běhu na pozadí pro funkce aktivní jízdy. Přesný rozsah vždy závisí na nastavení operačního systému a vašich oprávněních.',
        ],
      },
      {
        number: '7',
        title: 'Bezpečnost na vodě',
        paragraphs: [
          'Informace o sjízdnosti, stavech vody, jezech, rizicích, událostech nebo komunitních hlášeních jsou pouze orientační. Nemusí odrážet skutečný stav na místě.',
          'Před vyplutím ověřte aktuální podmínky z oficiálních zdrojů a na místě. Nepodceňujte počasí, průtok, uzávěry, lokální překážky, výbavu, zkušenost posádky ani pokyny správců toku a záchranných složek.',
          'Volitelný hlasový hlídač jezů je jen orientační upozornění přes systémový hlasový výstup zařízení. Nemusí zaznít včas nebo vůbec a nenahrazuje prohlídku jezu z břehu.',
        ],
      },
      {
        number: '8',
        title: 'Zprávy a oznámení',
        paragraphs: [
          'Zprávy v rámci jízdy slouží ke koordinaci posádky. Nepoužívejte je k obtěžování, výhrůžkám, spamu, nezákonnému obsahu ani sdílení citlivých údajů jiných osob bez souhlasu.',
          'Push oznámení jsou informační. Náhledy oznámení a datové payloady nemají obsahovat text soukromých zpráv. Doručení oznámení není garantované.',
        ],
      },
      {
        number: '9',
        title: 'Uživatelský obsah',
        paragraphs: [
          'Do aplikace můžete vkládat příběhy, fotografie, hlášení, události, komentáře nebo jiné informace. Odpovídáte za to, že k obsahu máte práva a že neporušuje práva jiných osob.',
          'Vložením obsahu poskytujete provozovateli nevýhradní oprávnění obsah zobrazit, technicky uložit, upravit pro formát aplikace, zálohovat a zpřístupnit v rozsahu nutném pro provoz služby.',
        ],
      },
      {
        number: '10',
        title: 'Zakázané chování',
        items: [
          'porušování právních předpisů nebo práv třetích osob',
          'vkládání nepravdivých bezpečnostních hlášení, která mohou ohrozit ostatní',
          'obtěžování, nenávistné projevy, výhrůžky, šikana nebo zveřejňování osobních údajů bez souhlasu',
          'automatizované vytěžování služby, obcházení bezpečnostních omezení nebo narušování provozu',
          'vkládání škodlivého kódu, spamu, reklamy mimo povolené partnerské formáty nebo podvodného obsahu',
        ],
      },
      {
        number: '11',
        title: 'Moderace a nahlašování',
        paragraphs: [
          'Vybrané typy obsahu lze nahlásit v aplikaci, pokud je u nich dostupné tlačítko nahlášení; ostatní obsah, chyby v mapě nebo uživatele lze nahlásit e-mailem na info@na-vodu.cz. Po ručním posouzení může provozovatel odstranit nebo skrýt obsah, upozornit uživatele, omezit přístup ke službě nebo účet zrušit, pokud to umožňuje aktuální provozní postup.',
          'Moderace se řídí komunitními pravidly. Pokud nesouhlasíte s rozhodnutím, můžete požádat o přezkum stejným kontaktem.',
        ],
      },
      {
        number: '12',
        title: 'Partnerský obsah a nabídky',
        paragraphs: [
          'Aplikace může zobrazovat nabídky půjčoven, kempů, služeb nebo jiných partnerů. Pokud není výslovně uvedeno jinak, smluvní vztah vzniká přímo mezi vámi a partnerem.',
          'Provozovatel neodpovídá za dostupnost, kvalitu ani cenu partnerských služeb mimo rozsah, který sám přímo poskytuje.',
        ],
      },
      {
        number: '13',
        title: 'Dostupnost služby',
        paragraphs: [
          'Služba je poskytována v dostupném stavu. Může být dočasně nedostupná kvůli údržbě, chybám, výpadkům mapových podkladů, internetu, push služeb, úložiště, poskytovatelů přihlášení nebo externích zdrojů dat.',
          'Některé funkce fungují offline omezeně. Offline data mohou být zastaralá a nemusí obsahovat poslední změny.',
        ],
      },
      {
        number: '14',
        title: 'Ukončení účtu a mazání dat',
        paragraphs: [
          'Účet můžete smazat v aplikaci nebo požádat o smazání e-mailem. Po smazání účtu odstraníme nebo anonymizujeme osobní údaje podle zásad ochrany osobních údajů a podle zákonných povinností.',
          'Některé technické záznamy mohou zůstat omezenou dobu v zálohách, auditních záznamech nebo agregovaných statistikách.',
        ],
      },
      {
        number: '15',
        title: 'Odpovědnost',
        paragraphs: [
          'Používáte službu na vlastní odpovědnost. Rozhodnutí vyplout, pokračovat v jízdě, přenášet jez, vstoupit do vody nebo spoléhat na trasu je vždy rozhodnutím posádky.',
          'Provozovatel neodpovídá za újmu způsobenou tím, že uživatel spoléhal na neaktuální, komunitní, orientační nebo technicky nedoručená data, pokud odpovědnost nelze podle práva vyloučit.',
        ],
      },
      {
        number: '16',
        title: 'Změny, právo a kontakt',
        paragraphs: [
          'Podmínky můžeme měnit, zejména při změně funkcí, právních požadavků nebo provozu služby. Podstatné změny oznámíme přiměřeným způsobem.',
          'Tyto podmínky se řídí právem České republiky. Máte-li dotaz nebo stížnost, napište na info@na-vodu.cz.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Zásady ochrany osobních údajů',
    lastUpdated: legalLastUpdated,
    intro: [
      'Tento dokument popisuje, jak Na Vodu zpracovává osobní údaje v mobilní aplikaci, na webu a v souvisejících službách.',
      'Nejdůležitější principy jsou dobrovolné sdílení polohy, omezené uchovávání provozních dat a možnost smazat účet přímo v aplikaci nebo přes podporu.',
    ],
    clauses: [
      {
        number: '1',
        title: 'Správce a kontakt',
        paragraphs: [
          dataControllerText,
          'Pověřenec pro ochranu osobních údajů není jmenován. Veškeré dotazy k soukromí posílejte na uvedený e-mail.',
        ],
      },
      {
        number: '2',
        title: 'Rozsah zpracování',
        paragraphs: [
          'Zpracováváme pouze údaje potřebné pro provoz aplikace, zabezpečení, plnění smluvních povinností, komunikaci s uživateli, ochranu práv a splnění zákonných povinností.',
          'Přesná sada údajů závisí na tom, které funkce používáte, jaká oprávnění udělíte v systému zařízení a zda jste přihlášení jako uživatel, host nebo člen posádky.',
          'Hlasový hlídač jezů používá systémovou syntézu řeči v zařízení. Kvůli této funkci aplikace nenahrává hlas, nepoužívá mikrofon a neposílá hlasová ani audio data provozovateli.',
        ],
      },
      {
        number: '3',
        title: 'Kategorie osobních údajů',
        table: {
          headers: ['Kategorie', 'Příklady údajů'],
          rows: [
            ['Účet a identita', 'identifikátor účtu, e-mail, jméno nebo přezdívka, poskytovatel přihlášení, stav profilu'],
            ['Profil a posádka', 'avatar, role v jízdě, přijaté a odeslané pozvánky, vazby mezi členy posádky'],
            ['Jízdy a plánování', 'název, termín, řeka, trasa, zastávky, nastavení viditelnosti, stav jízdy'],
            ['Poloha', 'aktuální nebo poslední známá poloha, poloha kapitána, poloha pro úkoly, poloha událostí a hlášení'],
            ['Mapové a offline údaje', 'vybrané mapové balíčky, stav stažení, filtry mapy a nastavení zobrazení'],
            ['Komunikace', 'zprávy v jízdě, metadata doručení, systémové notifikace a token zařízení pro push oznámení'],
            ['Obsah uživatelů', 'příběhy, fotografie, hlášení, události, reporty, případně metadata média'],
            ['Výdaje a vyrovnání', 'částky, plátci, účastníci rozpočítání, poznámky a vazby na jízdu'],
            ['Úkoly a odměny', 'splněné úkoly, body, redeemované nabídky, QR odpovědi, důkazní data, čas a případná poloha'],
            ['Hodnocení míst', 'hodnocení, komentáře, zobrazené jméno, vazba na místo a uživatele'],
            ['Technická data', 'IP adresa, identifikátory požadavků, typ zařízení, verze aplikace, chybové záznamy a diagnostika'],
            ['Podpora a práva', 'e-mailová komunikace, žádosti o smazání účtu, žádosti podle GDPR a záznam o jejich vyřízení'],
          ],
        },
      },
      {
        number: '4',
        title: 'Účely a právní základy',
        table: {
          headers: ['Účel', 'Právní základ'],
          rows: [
            ['provoz účtu, jízdy, posádky, zpráv a mapových funkcí', 'plnění smlouvy a poskytnutí služby'],
            ['výdaje, úkoly, odměny, recenze a komunitní funkce', 'plnění služby a oprávněný zájem na integritě společných záznamů'],
            ['dobrovolné sdílení polohy kapitána a polohové funkce', 'plnění služby a souhlas nebo oprávnění udělené v systému zařízení'],
            ['hlasová bezpečnostní upozornění hlídače jezů', 'plnění vyžádané funkce; hlas se nenahrává a mikrofon se nepoužívá'],
            ['bezpečnost, prevence zneužití, logy a diagnostika', 'oprávněný zájem na bezpečném provozu služby'],
            ['push oznámení a servisní komunikace', 'plnění služby, souhlas s oznámeními v zařízení nebo oprávněný zájem u servisních zpráv'],
            ['vyřízení práv, stížností a účetních či právních povinností', 'splnění právní povinnosti a ochrana právních nároků'],
            ['dobrovolný marketing nebo newsletter', 'souhlas, pokud bude taková funkce zapnuta'],
          ],
        },
      },
      {
        number: '5',
        title: 'Poloha a poloha na pozadí',
        paragraphs: [
          'Přesnou polohu zpracováváme jen tehdy, když ji povolíte v systému zařízení nebo když použijete funkci, která polohu vyžaduje. Oprávnění můžete kdykoli změnit v nastavení telefonu.',
          'Poloha kapitána může být při aktivní jízdě sdílena s posádkou nebo na mapě podle nastavení jízdy. Nově vytvářené jízdy nejsou veřejně sdílené na mapě bez výslovného zapnutí.',
          'Pokud povolíte polohu na pozadí, aplikace ji může použít i při zamčeném telefonu pro navazující funkce aktivní jízdy. Nepoužíváme ji k prodeji polohových dat ani k behaviorální reklamě.',
        ],
      },
      {
        number: '6',
        title: 'Zprávy a push oznámení',
        paragraphs: [
          'Zprávy slouží ke komunikaci v rámci jízdy. Provozovatel může zpracovávat obsah zpráv a metadata doručení v rozsahu nutném pro doručení, zobrazení, synchronizaci, bezpečnost a řešení nahlášení.',
          'Zprávy jsou při uložení v databázi šifrované serverovým klíčem. Nejde o end-to-end šifrování; server zprávy při doručení a zobrazení dešifruje.',
          'Push oznámení posíláme přes platformní služby zařízení. Náhled oznámení ani datový payload nemá obsahovat text soukromé zprávy.',
        ],
      },
      {
        number: '7',
        title: 'Příběhy, fotografie a komunitní obsah',
        paragraphs: [
          'Fotografie, příběhy, události, hlášení a reporty se mohou zobrazit ostatním uživatelům podle jejich povahy a nastavení. Nevkládejte obsah, ke kterému nemáte práva, ani podobizny osob bez odpovídajícího právního důvodu.',
          'Nahlášený obsah můžeme zkontrolovat, omezit nebo odstranit podle komunitních pravidel a zákonných povinností.',
        ],
      },
      {
        number: '8',
        title: 'Kdo může údaje vidět',
        paragraphs: [
          'Údaje o jízdě mohou vidět členové posádky, pozvaní uživatelé nebo veřejnost, pokud nastavíte veřejnou viditelnost. Některé mapové body, události, příběhy a hlášení mohou být veřejné nebo komunitní.',
          'Pracovníci a dodavatelé provozovatele mají přístup pouze v rozsahu nutném pro provoz, podporu, bezpečnost, moderaci a zákonné povinnosti.',
        ],
      },
      {
        number: '9',
        title: 'Zpracovatelé, příjemci a předávání',
        paragraphs: [
          'Používáme poskytovatele infrastruktury, úložišť, přihlášení, push oznámení, e-mailu, mapových podkladů a diagnostiky. Konkrétní seznam se může měnit podle provozu služby.',
        ],
        table: {
          headers: ['Oblast', 'Příklady příjemců'],
          rows: [
            ['hosting a serverová infrastruktura', 'Hetzner a navazující provozní nástroje'],
            ['média, CDN a ochrana provozu', 'Cloudflare R2, CDN a DNS služby'],
            ['přihlášení a mobilní platformy', 'Google, Apple a Meta/Facebook podle zvoleného způsobu přihlášení'],
            ['push oznámení', 'Apple Push Notification service a Firebase Cloud Messaging'],
            ['e-mail a podpora', 'poskytovatel e-mailových služeb a domény'],
            ['diagnostika a monitoring', 'Sentry, pokud je v daném sestavení zapnutý, a interní client error logy'],
            ['zálohy', 'šifrované restic zálohy a Cloudflare R2 úložiště pro záložní data'],
            ['mapy a veřejné podklady', 'poskytovatelé mapových dlaždic, stylů a souvisejících dat'],
          ],
        },
      },
      {
        number: '10',
        title: 'Doba uchování',
        table: {
          headers: ['Údaj', 'Typická doba uchování'],
          rows: [
            ['účet a profil', 'po dobu existence účtu a následně jen v nutném omezeném rozsahu'],
            ['jízdy, posádky a pozvánky', 'po dobu provozu služby nebo do smazání či anonymizace podle povahy záznamu'],
            ['poloha kapitána pro radar', 'živé záznamy typicky do 30 minut; poslední poloha uložená u jízdy může zůstat déle, zejména do ukončení jízdy nebo smazání/anonymizace'],
            ['zprávy v jízdě', 'po dobu existence konverzace nebo do odstranění při smazání účtu či z právních důvodů'],
            ['příběhy a média', 'podle zvolené životnosti příběhu, obvykle 1 až 24 hodin; CDN cache může krátce přetrvat po serverovém odstranění'],
            ['technické a chybové logy', 'typicky 30 až 90 dní, není-li nutné delší uchování pro bezpečnost nebo právní nároky'],
            ['zálohy', 'do rotace provozních záloh; po obnově zálohy se účtová mazání a anonymizace znovu aplikují v technicky možném rozsahu'],
          ],
        },
      },
      {
        number: '11',
        title: 'Smazání účtu',
        paragraphs: [
          'Účet lze smazat přímo v aplikaci nebo žádostí na info@na-vodu.cz. Po smazání odstraníme nebo anonymizujeme osobní údaje, které již nepotřebujeme pro právní povinnosti, bezpečnost, obranu nároků nebo zachování integrity společných záznamů.',
          'Aplikace se po smazání účtu odhlásí a odstraní místní cache, push token a lokální datové vazby v zařízení v rozsahu, který umí ovlivnit.',
        ],
      },
      {
        number: '12',
        title: 'Vaše práva',
        paragraphs: [
          'Máte právo na přístup, opravu, výmaz, omezení zpracování, přenositelnost, námitku proti zpracování na základě oprávněného zájmu a odvolání souhlasu, pokud je zpracování na souhlasu založeno.',
          'Žádost pošlete na info@na-vodu.cz. Máte také právo obrátit se na Úřad pro ochranu osobních údajů, Pplk. Sochora 27, 170 00 Praha 7, www.uoou.cz.',
        ],
      },
      {
        number: '13',
        title: 'Děti',
        paragraphs: [
          'Nezletilí mohou službu používat pouze se souhlasem a dohledem zákonného zástupce; dětem mladším 15 let není určena bez odpovídajícího právního důvodu nebo souhlasu. Pokud zjistíme, že zpracováváme osobní údaje dítěte bez odpovídajícího právního důvodu, přijmeme přiměřené kroky k nápravě.',
        ],
      },
      {
        number: '14',
        title: 'Zabezpečení',
        paragraphs: [
          'Používáme přiměřená technická a organizační opatření, zejména šifrovanou komunikaci, oddělení provozních přístupů, omezení přístupů, zálohy, monitoring chyb a bezpečnostní aktualizace.',
          'Žádný systém není zcela bez rizika. Pokud máte podezření na bezpečnostní incident, napište na info@na-vodu.cz.',
        ],
      },
      {
        number: '15',
        title: 'Změny zásad',
        paragraphs: [
          'Zásady můžeme aktualizovat při změně aplikace, dodavatelů nebo právních požadavků. Aktuální verze je dostupná na webu a v aplikaci.',
        ],
      },
    ],
  },
  safety: {
    title: 'Upozornění k bezpečnosti a sjízdnosti',
    lastUpdated: legalLastUpdated,
    intro: [
      'Toto upozornění shrnuje limity dat v aplikaci Na Vodu. Je součástí podmínek používání.',
    ],
    clauses: [
      {
        number: '1',
        title: 'Aplikace není bezpečnostní služba',
        paragraphs: [
          'Na Vodu není tísňová linka, záchranná služba, dispečink, oficiální hydrologická služba ani závazný navigační systém.',
          'V nouzi volejte 112, 150 nebo místní záchranné složky. Aplikace nemusí fungovat bez signálu, baterie, GPS, internetu nebo dostupných mapových služeb.',
        ],
      },
      {
        number: '2',
        title: 'Jezy a nebezpečná místa',
        paragraphs: [
          'Informace o jezech, propustích, válcích, přenášení, překážkách a dalších nebezpečných místech jsou orientační. Stav na místě se může změnit po povodni, stavbě, zásahu správce toku, pádu stromu nebo změně průtoku.',
          'Hlasová upozornění na jezy mohou být opožděná, neslyšitelná nebo vůbec nedostupná kvůli hlasitosti zařízení, režimu soustředění, výpadku systému, baterii nebo jinému technickému omezení.',
          'Před sjetím jezu vždy proveďte vlastní prohlídku z břehu a respektujte značení, uzávěry a pokyny místních autorit. Pokud si nejste jistí, jez přeneste.',
        ],
      },
      {
        number: '3',
        title: 'Sjízdnost a stavy vody',
        paragraphs: [
          'Údaje o sjízdnosti, průtoku, hladině a doporučení k jízdě mohou být zpožděné, neúplné nebo převzaté z externích a komunitních zdrojů.',
          'Sjízdnost závisí na zkušenosti posádky, typu lodě, výbavě, počasí, teplotě vody, lokálních překážkách a aktuálním stavu toku. Aplikace nemůže rozhodnout za vás.',
        ],
      },
      {
        number: '4',
        title: 'Mapy, hlášení a komunitní data',
        paragraphs: [
          'Mapové podklady, body zájmu, události a hlášení od uživatelů nemusí být přesné. Poloha bodu se může lišit od skutečnosti a některé informace mohou být zastaralé.',
          'Pokud najdete chybu, napište na info@na-vodu.cz; u vybraného obsahu je dostupné také tlačítko Nahlásit v aplikaci. Nahlášení samo o sobě neznamená okamžitou opravu.',
        ],
      },
      {
        number: '5',
        title: 'Poloha a komunikace',
        paragraphs: [
          'Sdílená poloha kapitána, zprávy a push oznámení mohou být opožděné nebo nedoručené. Nespoléhejte na ně jako na jediný způsob bezpečnostní koordinace.',
          'Domluvte si s posádkou záložní postupy pro rozdělení skupiny, ztrátu signálu, zranění, převržení, ztrátu vybavení a ukončení jízdy.',
        ],
      },
      {
        number: '6',
        title: 'Odpovědnost posádky',
        paragraphs: [
          'Za rozhodnutí vyplout, pokračovat, zastavit, přenést překážku nebo změnit trasu odpovídá posádka. Každý účastník má zohlednit své schopnosti, zdravotní stav, vybavení a aktuální podmínky.',
          'Používejte odpovídající plovací vesty a bezpečnostní vybavení. Nepodceňujte alkohol, únavu, chlad, vysokou vodu, nízkou vodu ani počasí.',
        ],
      },
      {
        number: '7',
        title: 'Doporučené ověření',
        items: [
          'ověřte stav vody a výstrahy z oficiálních zdrojů',
          'zkontrolujte předpověď počasí a vývoj průtoků',
          'ověřte uzávěry, stavební práce a místní zákazy',
          'prohlédněte riziková místa z břehu',
          'mějte nabitý telefon a plán pro případ ztráty signálu',
        ],
      },
    ],
  },
  community: {
    title: 'Komunitní pravidla a nahlašování obsahu',
    lastUpdated: legalLastUpdated,
    intro: [
      'Na Vodu má být praktický nástroj pro posádky a vodáckou komunitu. Tato pravidla určují, co do služby patří, co je zakázané a jak řešíme nahlášení.',
    ],
    clauses: [
      {
        number: '1',
        title: 'Základní pravidlo',
        paragraphs: [
          'Používejte službu tak, aby pomáhala ostatním, nezvyšovala riziko na vodě a nezasahovala neoprávněně do práv jiných osob.',
        ],
      },
      {
        number: '2',
        title: 'Zakázaný obsah',
        items: [
          'výhrůžky, šikana, obtěžování, nenávistné projevy a podněcování k násilí',
          'nepravdivá nebo zavádějící bezpečnostní hlášení, která mohou ohrozit ostatní',
          'osobní údaje, poloha, fotografie nebo podobizna jiné osoby bez právního důvodu',
          'pornografický, extrémně násilný, nezákonný nebo zjevně škodlivý obsah',
          'spam, podvody, malware, nevyžádaná reklama nebo manipulativní nabídky',
          'obsah porušující autorská práva, ochranné známky nebo jiná práva třetích osob',
        ],
      },
      {
        number: '3',
        title: 'Poloha, fotografie a soukromí ostatních',
        paragraphs: [
          'Nezveřejňujte přesnou polohu osob, posádek, ubytování nebo vybavení, pokud k tomu nemáte oprávnění a nejde o očekávanou funkci jízdy.',
          'Fotografie vkládejte pouze tehdy, pokud máte právo je zveřejnit a respektujete soukromí zachycených osob.',
        ],
      },
      {
        number: '4',
        title: 'Jak nahlášení podat',
        paragraphs: [
          'Vybrané typy obsahu lze nahlásit přímo v aplikaci, pokud je u dané funkce dostupné tlačítko nahlášení. Ostatní obsah, uživatele nebo chyby v datech můžete nahlásit e-mailem na info@na-vodu.cz.',
          'Uveďte prosím, čeho se nahlášení týká, proč je obsah problematický, odkaz nebo identifikaci obsahu a případné doplňující informace.',
        ],
      },
      {
        number: '5',
        title: 'Jak nahlášení posuzujeme',
        paragraphs: [
          'Nahlášení posuzujeme podle těchto pravidel, podmínek služby a právních předpisů. Zohledňujeme kontext, riziko pro bezpečnost, práva dotčených osob a závažnost porušení.',
          'Moderace probíhá ručně podle aktuálních provozních možností. Automatické mechanismy mohou pomoci s technickým tříděním, ale rozhodnutí o sporném obsahu provádí člověk nebo provozovatel služby.',
        ],
      },
      {
        number: '6',
        title: 'Možná opatření',
        paragraphs: [
          'Po ručním posouzení může provozovatel podle závažnosti a dostupných provozních postupů přijmout zejména tato opatření.',
        ],
        items: [
          'ponechání obsahu bez zásahu',
          'omezení dosahu nebo dočasné skrytí obsahu',
          'odstranění obsahu',
          'upozornění uživatele',
          'dočasné omezení funkcí nebo účtu',
          'zrušení účtu při závažném nebo opakovaném porušení',
          'předání věci příslušným orgánům, pokud to vyžaduje právo nebo bezpečnost',
        ],
      },
      {
        number: '7',
        title: 'Přezkum rozhodnutí',
        paragraphs: [
          'Pokud nesouhlasíte s moderátorským rozhodnutím, napište na info@na-vodu.cz a uveďte, čeho se rozhodnutí týká a proč s ním nesouhlasíte.',
          'Žádost o přezkum posoudíme přiměřeně povaze věci. U zjevně šikanózních, opakovaných nebo neúplných žádostí můžeme odpověď omezit.',
        ],
      },
      {
        number: '8',
        title: 'Opakované zneužívání',
        paragraphs: [
          'Opakované porušování pravidel, zneužívání nahlášení, falešné bezpečnostní reporty nebo pokusy poškodit službu mohou vést k omezení nebo zrušení účtu.',
        ],
      },
      {
        number: '9',
        title: 'Kontakt podle DSA',
        paragraphs: [
          'Kontaktním místem pro oznámení, moderaci obsahu a komunikaci podle nařízení o digitálních službách je info@na-vodu.cz.',
          'Komunikovat s námi můžete česky nebo anglicky. Oznámení musí být dostatečně konkrétní, abychom mohli obsah dohledat a posoudit.',
        ],
      },
    ],
  },
  cookies: {
    title: 'Cookies a podobné technologie',
    lastUpdated: legalLastUpdated,
    intro: [
      'Tento dokument vysvětluje používání cookies na webu Na Vodu a lokálních úložišť v aplikaci.',
    ],
    clauses: [
      {
        number: '1',
        title: 'Rozsah',
        paragraphs: [
          'Web na-vodu.cz a mobilní aplikace mohou používat cookies, lokální úložiště, cache, identifikátory zařízení a podobné technologie.',
          'Tyto technologie slouží k tomu, aby web a aplikace fungovaly, pamatovaly si nastavení a umožnily bezpečné přihlášení nebo synchronizaci.',
        ],
      },
      {
        number: '2',
        title: 'Současné používání na webu',
        paragraphs: [
          'Veřejný marketingový web je navržený tak, aby nepotřeboval reklamní ani behaviorální marketingové cookies. Pokud budou zapnuty analytické nebo marketingové nástroje, doplníme informace a vyžádáme souhlas tam, kde je potřeba.',
        ],
      },
      {
        number: '3',
        title: 'Nezbytné technologie',
        table: {
          headers: ['Technologie', 'Účel'],
          rows: [
            ['technické cookies a cache', 'načtení webu, zabezpečení, výkon a stabilita'],
            ['lokální úložiště aplikace', 'offline režim, mapové balíčky, nastavení, dočasná data a přihlášení'],
            ['systémová syntéza řeči', 'přehrání volitelných hlasových upozornění v zařízení; nejde o mikrofon ani nahrávání zvuku'],
            ['push token zařízení', 'doručení servisních oznámení, pokud je povolíte'],
            ['diagnostické záznamy', 'oprava chyb, bezpečnost a stabilita služby'],
          ],
        },
      },
      {
        number: '4',
        title: 'Přihlášení a platformní služby',
        paragraphs: [
          'Při přihlášení přes externí poskytovatele mohou Google, Apple, Meta/Facebook nebo systém zařízení používat vlastní cookies, SDK identifikátory nebo obdobné technologie podle svých pravidel.',
          'Tyto technologie neřídíme v plném rozsahu. Jejich nastavení spravujete v účtu daného poskytovatele, prohlížeči nebo v systému zařízení.',
        ],
      },
      {
        number: '5',
        title: 'Mobilní aplikace',
        paragraphs: [
          'Aplikace ukládá data lokálně v zařízení, například cache jízd, mapové podklady, offline data, stav přihlášení, nastavení filtrů a dočasné fronty synchronizace.',
          'Po odhlášení aplikace zruší lokální relaci. Po smazání účtu se v podporovaném rozsahu odstraní také lokální data navázaná na účet. Některá data může odstranit také systém zařízení nebo uživatel odinstalací aplikace.',
        ],
      },
      {
        number: '6',
        title: 'Správa souhlasů',
        paragraphs: [
          'Souhlasy s oznámeními, polohou a dalšími oprávněními spravujete v nastavení operačního systému. Hlasová upozornění používají nastavení zvuku zařízení a nevyžadují oprávnění k mikrofonu. Cookies webu lze spravovat v prohlížeči.',
          'Pokud později zapneme nástroje vyžadující samostatný cookies souhlas, zobrazíme odpovídající volbu před jejich použitím.',
        ],
      },
      {
        number: '7',
        title: 'Kontakt',
        paragraphs: ['Dotazy ke cookies a podobným technologiím posílejte na info@na-vodu.cz.'],
      },
    ],
  },
  'account-delete': {
    title: 'Smazání účtu a dat',
    lastUpdated: legalLastUpdated,
    intro: [
      'Účet Na Vodu můžete smazat přímo v aplikaci nebo požádat o smazání e-mailem. Tato stránka popisuje praktický postup a dopady smazání.',
    ],
    clauses: [
      {
        number: '1',
        title: 'Smazání v aplikaci',
        paragraphs: [
          'V přihlášené aplikaci otevřete Profil a použijte akci Smazat účet. Aplikace vás požádá o potvrzení, protože smazání je zásadní zásah do účtu a dat.',
          'Po dokončení se aplikace odhlásí, zneplatní lokální relaci a odstraní místní cache v zařízení v rozsahu podporovaném aplikací.',
        ],
      },
      {
        number: '2',
        title: 'Smazání přes podporu',
        paragraphs: [
          'Pokud se do aplikace nedostanete, napište z e-mailu spojeného s účtem na info@na-vodu.cz a do předmětu uveďte žádost o smazání účtu Na Vodu.',
          'Můžeme požádat o ověření totožnosti nebo vlastnictví účtu, aby účet nesmazala neoprávněná osoba.',
        ],
        actions: [
          {
            label: 'Požádat e-mailem o smazání účtu',
            href: 'mailto:info@na-vodu.cz?subject=Smazani%20uctu%20Na%20Vodu',
          },
        ],
      },
      {
        number: '3',
        title: 'Lhůta zpracování',
        paragraphs: [
          'Žádosti řešíme bez zbytečného odkladu, obvykle do 30 dnů. U složitějších žádostí nebo při nutnosti ověření vás budeme kontaktovat.',
        ],
      },
      {
        number: '4',
        title: 'Co se smaže nebo anonymizuje',
        table: {
          headers: ['Oblast', 'Dopad smazání'],
          rows: [
            ['profil a přihlášení', 'odstranění účtu, relace, push tokenu a osobních profilových vazeb'],
            ['jízdy a posádky', 'odstranění nebo anonymizace osobních vazeb, pokud to nenaruší práva ostatních členů nebo historický kontext jízdy'],
            ['poloha', 'odstranění aktivních polohových záznamů a ukončení sdílení polohy'],
            ['zprávy', 'smazání nebo anonymizace podle technických možností a práv ostatních účastníků konverzace'],
            ['příběhy, fotografie a reporty', 'odstranění, odpojení nebo anonymizace obsahu navázaného na účet; veřejné objekty nebo CDN cache mohou krátce přetrvat podle technických možností úložiště'],
            ['technické logy', 'uchování pouze po omezenou dobu pro bezpečnost, diagnostiku a právní nároky'],
          ],
        },
      },
      {
        number: '5',
        title: 'Co může zůstat',
        paragraphs: [
          'Některé údaje můžeme dočasně uchovat, pokud to vyžaduje zákon, bezpečnost služby, obrana právních nároků, prevence zneužití nebo rotace záloh.',
          'Moderátorské a právní auditní záznamy mohou v nezbytném rozsahu obsahovat kontext nahlášení nebo identifikaci cílového obsahu, aby bylo možné řešit zneužití a právní povinnosti.',
          'Agregované statistiky, které již neumožňují identifikovat konkrétní osobu, mohou zůstat zachovány.',
        ],
      },
      {
        number: '6',
        title: 'Lokální data v zařízení',
        paragraphs: [
          'Aplikace se při smazání účtu snaží odstranit lokální cache, offline data navázaná na účet, fronty synchronizace a tokeny. Data mimo kontrolu aplikace můžete odstranit také odinstalací aplikace nebo vymazáním dat aplikace v systému zařízení.',
        ],
      },
      {
        number: '7',
        title: 'Kontakt a stížnosti',
        paragraphs: [
          'Dotazy ke smazání účtu posílejte na info@na-vodu.cz. Pokud nejste spokojeni s vyřízením žádosti o osobní údaje, můžete se obrátit na Úřad pro ochranu osobních údajů, www.uoou.cz.',
        ],
      },
    ],
  },
};
function PhoneShot({
  shot,
  className = '',
  priority = false,
}: {
  shot: Shot;
  className?: string;
  priority?: boolean;
}) {
  return (
    <figure className={`phone-shot ${className}`}>
      <div className="phone-shot-frame">
        <img src={shot.src} alt={shot.alt} loading={priority ? 'eager' : 'lazy'} />
      </div>
      <figcaption>
        <span>{shot.label}</span>
        <strong>{shot.title}</strong>
      </figcaption>
    </figure>
  );
}

type StorePlatform = 'apple' | 'google';

function StoreIcon({ platform }: { platform: StorePlatform }) {
  if (platform === 'apple') {
    return (
      <svg aria-hidden="true" className="store-icon" viewBox="0 0 32 32">
        <path
          d="M22.41 17.05a5.15 5.15 0 0 1 2.45-4.32 5.27 5.27 0 0 0-4.15-2.25c-1.75-.18-3.44 1.04-4.33 1.04-.91 0-2.28-1.03-3.75-1a5.53 5.53 0 0 0-4.65 2.84c-2.01 3.49-.51 8.61 1.42 11.43.96 1.38 2.09 2.92 3.57 2.86 1.44-.06 1.98-.92 3.72-.92 1.73 0 2.23.92 3.74.89 1.55-.03 2.52-1.39 3.45-2.78a11.41 11.41 0 0 0 1.58-3.22 4.98 4.98 0 0 1-3.05-4.57Z"
          fill="currentColor"
        />
        <path
          d="M19.57 8.62a5.07 5.07 0 0 0 1.16-3.63 5.16 5.16 0 0 0-3.34 1.73 4.83 4.83 0 0 0-1.19 3.5 4.27 4.27 0 0 0 3.37-1.6Z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" className="store-icon store-icon--google" viewBox="0 0 44 40">
      <path d="M7.6 3.9a5 5 0 0 0-.2 1.4v29.4c0 .48.07.96.2 1.4L23.8 20 7.6 3.9Z" fill="#5778c5" />
      <path d="m24.2 20 8.12-8.1L14.7 1.7a4.17 4.17 0 0 0-5.9 2.18L24.2 20Z" fill="#3bad49" />
      <path d="m24.2 20-15.4 16.2a4.17 4.17 0 0 0 5.88 2.1l17.64-10.2L24.2 20Z" fill="#eb3131" />
      <path d="m40 16.3-.02-.01-7.65-4.4L23.8 20l8.52 8.1 7.62-4.39A4.2 4.2 0 0 0 40 16.3Z" fill="#f6b60b" />
    </svg>
  );
}

function CleanStoreLink({ platform }: { platform: StorePlatform }) {
  const label = platform === 'apple' ? 'App Store' : 'Google Play';
  const storeUrl = platform === 'apple' ? appStoreUrl : googlePlayUrl;
  const actionLabel = platform === 'apple' ? 'Stáhnout v' : 'Stáhnout na';
  const ariaLabel =
    platform === 'apple'
      ? 'Stáhnout Na Vodu v App Storu'
      : 'Stáhnout Na Vodu na Google Play';

  return (
    <a
      aria-label={ariaLabel}
      className={`store-link store-link--${platform}`}
      href={storeUrl}
      rel="noreferrer"
      target="_blank"
    >
      <StoreIcon platform={platform} />
      <span className="store-copy">
        <span>{actionLabel}</span>
        <strong>{label}</strong>
      </span>
    </a>
  );
}

function detectStorePlatform(navigatorRef: Navigator): StorePlatform | null {
  const userAgent = navigatorRef.userAgent.toLowerCase();
  const isAppleTouchDevice =
    navigatorRef.platform === 'MacIntel' && navigatorRef.maxTouchPoints > 1;

  if (/iphone|ipad|ipod/.test(userAgent) || isAppleTouchDevice) {
    return 'apple';
  }
  if (/android/.test(userAgent)) {
    return 'google';
  }
  return null;
}

function DownloadLanding() {
  const [detectedPlatform, setDetectedPlatform] = useState<StorePlatform | null>(
    null,
  );

  useEffect(() => {
    const platform = detectStorePlatform(window.navigator);

    if (!platform) {
      return;
    }

    setDetectedPlatform(platform);
    window.location.replace(platform === 'apple' ? appStoreUrl : googlePlayUrl);
  }, []);

  const detectedStore =
    detectedPlatform === 'apple'
      ? 'App Store'
      : detectedPlatform === 'google'
        ? 'Google Play'
        : null;

  return (
    <main className="join-page download-page">
      <section className="join-panel download-panel" aria-labelledby="download-title">
        <img src="/assets/logo-current-n.svg" alt="" />
        <p className="eyebrow">stažení aplikace</p>
        <h1 id="download-title">Stáhnout Na Vodu.</h1>
        <p>
          Stáhni si appku před vyplutím. Měj po ruce mapu Vltavy, úkoly po
          trase a odměny u vybraných partnerů.
        </p>
        {detectedStore ? (
          <p className="download-status" aria-live="polite">
            Přesměrováváme do {detectedStore}.
          </p>
        ) : null}
        <div className="download-store-list" aria-label="Odkazy ke stažení">
          <CleanStoreLink platform="apple" />
          <CleanStoreLink platform="google" />
        </div>
        <a className="download-home-link" href="/">
          Zpět na web
        </a>
      </section>
    </main>
  );
}

function SocialIcon({ platform }: { platform: SocialPlatform }) {
  const Icon = platform === 'instagram' ? FaInstagram : FaFacebookF;

  return <Icon aria-hidden="true" />;
}

function SocialLinks({ variant = 'inline' }: { variant?: 'inline' | 'footer' }) {
  return (
    <div
      aria-label="Sociální sítě Na Vodu"
      className={`social-links social-links--${variant}`}
    >
      {socialLinks.map((link) => (
        <a
          aria-label={`${link.label} Na Vodu`}
          className={`social-link social-link--${link.id}`}
          href={link.href}
          key={link.id}
          rel="noreferrer"
          target="_blank"
        >
          <SocialIcon platform={link.id} />
          <span>{variant === 'footer' ? link.label : link.handle}</span>
        </a>
      ))}
    </div>
  );
}

function JoinInviteLanding({ origin, pathname }: { origin: string; pathname: string }) {
  const token = decodeURIComponent(pathname.split('/')[2] ?? '');
  const inviteUrl = `${origin}/join/${encodeURIComponent(token)}`;

  return (
    <main className="join-page">
      <section className="join-panel" aria-labelledby="join-title">
        <img src="/assets/logo-current-n.svg" alt="" />
        <p className="eyebrow">pozvánka do jízdy</p>
        <h1 id="join-title">Připojit se v aplikaci Na Vodu.</h1>
        <p>
          Tohle je pozvánka od kapitána posádky. Pokud máš aplikaci
          nainstalovanou, otevři ji tímhle odkazem a potvrď připojení.
        </p>
        <div className="join-actions">
          <a className="join-primary" href={inviteUrl}>
            Otevřít v aplikaci
          </a>
          <a className="join-secondary" href="/#start">
            Stáhnout aplikaci
          </a>
        </div>
        <p className="join-code">
          Kód: <strong>{token || 'neznámý'}</strong>
        </p>
      </section>
    </main>
  );
}

function MagicLinkLanding({
  origin,
  pathname,
  search,
}: {
  origin: string;
  pathname: string;
  search: string;
}) {
  const magicLinkUrl = `${origin}${pathname}${search}`;

  return (
    <main className="join-page">
      <section className="join-panel" aria-labelledby="magic-link-title">
        <img src="/assets/logo-current-n.svg" alt="" />
        <p className="eyebrow">přihlášení e-mailem</p>
        <h1 id="magic-link-title">Otevřít přihlášení v aplikaci Na Vodu.</h1>
        <p>
          Pokud máš aplikaci nainstalovanou, tenhle odkaz tě přihlásí přímo v ní.
          Když se aplikace neotevře automaticky, zkus tlačítko znovu z mobilu.
        </p>
        <div className="join-actions">
          <a className="join-primary" href={magicLinkUrl}>
            Otevřít v aplikaci
          </a>
          <a className="join-secondary" href="/#start">
            Stáhnout aplikaci
          </a>
        </div>
      </section>
    </main>
  );
}

function LegalStaticPage({ page }: { page: LegalPageContent }) {
  return (
    <main className="legal-document-page">
      <article className="legal-document" aria-labelledby="legal-title">
        <nav className="legal-document-nav" aria-label="Navigace právního dokumentu">
          <a href="/">
            Na Vodu
          </a>
        </nav>
        <header className="legal-document-header">
          <h1 id="legal-title">{page.title}</h1>
          <p className="legal-document-meta">Aktualizováno: {page.lastUpdated}</p>
          {page.intro?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </header>
        {page.clauses.map((clause) => (
          <section
            className="legal-clause"
            id={`clause-${clause.number.replace(/\./g, '-')}`}
            key={`${clause.number}-${clause.title}`}
          >
            <h2>
              <span>{clause.number}.</span> {clause.title}
            </h2>
            {clause.paragraphs?.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {clause.items ? (
              <ul>
                {clause.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            {clause.table ? (
              <div className="legal-table-wrap">
                <table>
                  <thead>
                    <tr>
                      {clause.table.headers.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {clause.table.rows.map((row) => (
                      <tr key={row.join('|')}>
                        {row.map((cell, cellIndex) => (
                          <td key={`${cellIndex}-${cell}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {clause.actions ? (
              <p className="legal-actions">
                {clause.actions.map((action) => (
                  <a href={action.href} key={action.href}>
                    {action.label}
                  </a>
                ))}
              </p>
            ) : null}
          </section>
        ))}
        <p className="legal-document-footnote">
          Na Vodu
        </p>
      </article>
    </main>
  );
}

function legalPageFromPath(pathname: string): LegalPageContent | null {
  if (pathname === '/account/delete') {
    return legalPages['account-delete'];
  }
  if (pathname === '/legal/terms') {
    return legalPages.terms;
  }
  if (pathname === '/legal/privacy') {
    return legalPages.privacy;
  }
  if (pathname === '/legal/safety') {
    return legalPages.safety;
  }
  if (pathname === '/legal/community') {
    return legalPages.community;
  }
  if (pathname === '/legal/cookies') {
    return legalPages.cookies;
  }
  return null;
}

function normalizePathname(pathname: string) {
  const cleanPathname = pathname.split('?')[0]?.split('#')[0] ?? '/';
  if (cleanPathname === '/') {
    return '/';
  }
  return cleanPathname.replace(/\/+$/, '') || '/';
}

function commonSchema() {
  return [
    {
      '@type': 'Organization',
      '@id': `${siteBaseUrl}/#organization`,
      name: 'Na Vodu',
      url: `${siteBaseUrl}/`,
      logo: `${siteBaseUrl}/assets/app-icon.png`,
      email: 'info@na-vodu.cz',
      sameAs: [instagramUrl, facebookUrl],
    },
    {
      '@type': 'WebSite',
      '@id': `${siteBaseUrl}/#website`,
      url: `${siteBaseUrl}/`,
      name: 'Na Vodu',
      description:
        'Oficiální web mobilní aplikace Na Vodu pro české vodáky.',
      inLanguage: 'cs-CZ',
      publisher: {
        '@id': `${siteBaseUrl}/#organization`,
      },
    },
  ];
}

export function getPageSeo(pathname: string): PageSeo {
  const normalizedPathname = normalizePathname(pathname);
  const canonicalUrl =
    normalizedPathname === '/'
      ? `${siteBaseUrl}/`
      : `${siteBaseUrl}${normalizedPathname}/`;
  const legalPage = legalPageFromPath(normalizedPathname);

  if (normalizedPathname === '/stahnout') {
    const title = 'Stáhnout aplikaci Na Vodu | App Store a Google Play';
    const description =
      'Stáhni si aplikaci Na Vodu pro iPhone nebo Android. Jeden odkaz pro App Store i Google Play.';

    return {
      canonicalUrl,
      dateModified: homeModifiedDate,
      datePublished: homePublishedDate,
      description,
      schemaGraph: [
        ...commonSchema(),
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          url: canonicalUrl,
          name: title,
          description,
          dateModified: homeModifiedDate,
          inLanguage: 'cs-CZ',
          isPartOf: {
            '@id': `${siteBaseUrl}/#website`,
          },
          mainEntity: {
            '@id': `${siteBaseUrl}/#app`,
          },
        },
        {
          '@type': 'SoftwareApplication',
          '@id': `${siteBaseUrl}/#app`,
          name: 'Na Vodu',
          applicationCategory: 'TravelApplication',
          operatingSystem: 'iOS, Android',
          inLanguage: 'cs-CZ',
          url: `${siteBaseUrl}/stahnout/`,
          sameAs: [appStoreUrl, googlePlayUrl],
          downloadUrl: [appStoreUrl, googlePlayUrl],
          image: `${siteBaseUrl}/assets/app-icon.png`,
          isAccessibleForFree: true,
          publisher: {
            '@id': `${siteBaseUrl}/#organization`,
          },
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'CZK',
          },
        },
      ],
      title,
      twitterTitle: 'Stáhnout Na Vodu | App Store a Google Play',
    };
  }

  if (legalPage) {
    const description =
      legalPage.intro?.[0] ??
      `${legalPage.title} pro aplikaci Na Vodu.`;

    return {
      canonicalUrl,
      dateModified: legalModifiedDate,
      description,
      schemaGraph: [
        ...commonSchema(),
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          url: canonicalUrl,
          name: legalPage.title,
          description,
          dateModified: legalModifiedDate,
          inLanguage: 'cs-CZ',
          isPartOf: {
            '@id': `${siteBaseUrl}/#website`,
          },
        },
      ],
      title: `${legalPage.title} | Na Vodu`,
      twitterTitle: `${legalPage.title} | Na Vodu`,
    };
  }

  const title = 'Vodácká aplikace pro plánování jízdy po Vltavě | Na Vodu';
  const description =
    'Naplánuj si vodáckou jízdu po Vltavě, sdílej polohu s ostatními vodáky, sbírej body a odměny, používej offline mapy, zprávy a kalkulačku výdajů.';
  const ogDescription =
    'Plán jízdy, říční radar, sdílení polohy, zprávy, příběhy, body, odměny, offline mapy a výdaje pro české vodáky. První sezona začíná na Vltavě.';

  return {
    canonicalUrl: `${siteBaseUrl}/`,
    dateModified: homeModifiedDate,
    datePublished: homePublishedDate,
    description,
    keywords:
      'Na Vodu, vodácká aplikace, vodáci, Vltava, plánování vodácké jízdy, říční radar, vodácká mapa, offline mapy Vltavy, odměny pro vodáky, výdaje posádky, vodácké kempy',
    ogDescription,
    schemaGraph: [
      ...commonSchema(),
      {
        '@type': 'WebPage',
        '@id': `${siteBaseUrl}/#webpage`,
        url: `${siteBaseUrl}/`,
        name: title,
        description:
          'Prezentační web aplikace Na Vodu pro české vodáky. Ukazuje plánování jízdy, říční radar, sdílení polohy, zprávy, příběhy, odměny, offline mapy, výdaje a hodnocení podniků.',
        datePublished: homePublishedDate,
        dateModified: homeModifiedDate,
        inLanguage: 'cs-CZ',
        isPartOf: {
          '@id': `${siteBaseUrl}/#website`,
        },
        mainEntity: {
          '@id': `${siteBaseUrl}/#app`,
        },
        about: {
          '@id': `${siteBaseUrl}/#app`,
        },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: `${siteBaseUrl}/assets/og-na-vodu.jpg`,
          width: 1200,
          height: 630,
        },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${siteBaseUrl}/#app`,
        name: 'Na Vodu',
        applicationCategory: 'TravelApplication',
        operatingSystem: 'iOS, Android',
        inLanguage: 'cs-CZ',
        description:
          'Vodácká aplikace pro plánování jízdy po Vltavě, říční radar, sdílení polohy, zprávy, příběhy, body, odměny, offline mapy, výdaje a hodnocení kempů a restaurací.',
        url: `${siteBaseUrl}/`,
        sameAs: [appStoreUrl, googlePlayUrl],
        downloadUrl: [appStoreUrl, googlePlayUrl],
        image: `${siteBaseUrl}/assets/app-icon.png`,
        screenshot: [
          {
            '@type': 'ImageObject',
            url: `${siteBaseUrl}${screenShots.radarMap.src}`,
            name: screenShots.radarMap.title,
            caption: screenShots.radarMap.alt,
          },
          {
            '@type': 'ImageObject',
            url: `${siteBaseUrl}${screenShots.plannerCreate.src}`,
            name: screenShots.plannerCreate.title,
            caption: screenShots.plannerCreate.alt,
          },
          {
            '@type': 'ImageObject',
            url: `${siteBaseUrl}${screenShots.tasksList.src}`,
            name: screenShots.tasksList.title,
            caption: screenShots.tasksList.alt,
          },
        ],
        keywords:
          'vodácká aplikace, Vltava, vodáci, plánování jízdy, říční radar, offline mapy, výdaje posádky',
        isAccessibleForFree: true,
        areaServed: {
          '@type': 'Country',
          name: 'Česko',
        },
        featureList: [
          'plánování vodácké jízdy',
          'sdílení polohy s ostatními vodáky',
          'zprávy mezi posádkami',
          'příběhy pro posádky v okolí',
          'body a odměny u partnerů',
          'offline mapy okolí Vltavy',
          'kalkulačka výdajů',
          'hodnocení kempů a restaurací',
        ],
        publisher: {
          '@id': `${siteBaseUrl}/#organization`,
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'CZK',
        },
      },
    ],
    title,
    twitterTitle: 'Vodácká aplikace pro Vltavu | Na Vodu',
  };
}

function App({ origin, pathname, search }: AppProps = {}) {
  const [activeFeatureId, setActiveFeatureId] = useState(featureItems[0].id);
  const [menuOpen, setMenuOpen] = useState(false);
  const browserLocation =
    typeof window === 'undefined' ? null : window.location;
  const routePathname = normalizePathname(
    pathname ?? browserLocation?.pathname ?? '/',
  );
  const routeSearch = search ?? browserLocation?.search ?? '';
  const routeOrigin = origin ?? browserLocation?.origin ?? siteBaseUrl;
  const activeFeature =
    featureItems.find((feature) => feature.id === activeFeatureId) ?? featureItems[0];
  const { radarMap, plannerCreate, tasksList } = screenShots;

  if (routePathname.startsWith('/join/')) {
    return <JoinInviteLanding origin={routeOrigin} pathname={routePathname} />;
  }
  if (routePathname === '/auth/magic') {
    return (
      <MagicLinkLanding
        origin={routeOrigin}
        pathname={routePathname}
        search={routeSearch}
      />
    );
  }
  if (routePathname === '/stahnout') {
    return <DownloadLanding />;
  }
  const legalPage = legalPageFromPath(routePathname);
  if (legalPage) {
    return <LegalStaticPage page={legalPage} />;
  }

  return (
    <div className="site-shell">
      <header className={`site-header ${menuOpen ? 'is-open' : ''}`}>
        <a
          className="brand"
          href="#uvod"
          aria-label="Na Vodu domů"
          onClick={() => setMenuOpen(false)}
        >
          <img src="/assets/logo-current-n.svg" alt="" />
          <span>Na Vodu</span>
        </a>
        <button
          aria-controls="site-nav"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Zavřít menu' : 'Otevřít menu'}
          className="menu-toggle"
          onClick={() => setMenuOpen((open) => !open)}
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
        <nav aria-label="Hlavní navigace" className="site-nav" id="site-nav">
          <a href="#appka" onClick={() => setMenuOpen(false)}>
            Appka
          </a>
          <a href="#ukazky" onClick={() => setMenuOpen(false)}>
            Ukázky
          </a>
          <a href="#vltava" onClick={() => setMenuOpen(false)}>
            Vltava
          </a>
          <a href="#partneri" onClick={() => setMenuOpen(false)}>
            Partneři
          </a>
          <a href="#start" onClick={() => setMenuOpen(false)}>
            Stažení
          </a>
          <a href="mailto:info@na-vodu.cz" onClick={() => setMenuOpen(false)}>
            Kontakt
          </a>
        </nav>
      </header>

      <main>
        <section className="hero-section" id="uvod">
          <div className="hero-copy">
            <img
              className="hero-logo"
              src="/assets/logo-current-n-stacked.svg"
              alt=""
              aria-hidden="true"
            />
            <p className="eyebrow">vodácká aplikace</p>
            <h1>Na Vodu</h1>
            <p>
              Naplánuj si jízdu. Sbírej body a odměny u partnerů. Sdílej polohu,
              piš zprávy, přidávej příběhy, počítej výdaje a hodnoť kempy i
              restaurace.
            </p>
            <div className="hero-actions">
              <a href="#appka">Prohlédnout</a>
              <a href="#start">Ke stažení</a>
            </div>
            <SocialLinks />
          </div>

          <div className="hero-screens" aria-label="Ukázky aplikace Na Vodu">
            <PhoneShot shot={radarMap} className="hero-main-shot" priority />
            <PhoneShot shot={plannerCreate} className="hero-side-shot hero-side-one" />
            <PhoneShot shot={tasksList} className="hero-side-shot hero-side-two" />
          </div>
        </section>

        <section className="feature-switcher" id="appka" aria-labelledby="features-title">
          <div className="feature-intro">
            <p className="eyebrow">funkce</p>
            <h2 id="features-title">Co všechno appka umí.</h2>
          </div>

          <div className="feature-layout">
            <div className="feature-tabs" role="tablist" aria-label="Funkce aplikace">
              {featureItems.map((feature, index) => {
                const active = feature.id === activeFeature.id;

                return (
                  <button
                    aria-controls="feature-panel"
                    aria-selected={active}
                    className={`feature-tab ${active ? 'is-active' : ''}`}
                    id={`feature-tab-${feature.id}`}
                    key={feature.id}
                    onClick={() => setActiveFeatureId(feature.id)}
                    role="tab"
                    type="button"
                  >
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <strong>{feature.nav}</strong>
                  </button>
                );
              })}
            </div>

            <article
              aria-labelledby={`feature-tab-${activeFeature.id}`}
              className="feature-detail"
              id="feature-panel"
              role="tabpanel"
            >
              <p className="eyebrow">{activeFeature.kicker}</p>
              <h3>{activeFeature.title}</h3>
              <p>{activeFeature.text}</p>
            </article>

            <div className="feature-visual" aria-live="polite">
              <PhoneShot
                key={activeFeature.id}
                shot={activeFeature.shot}
                className="feature-phone"
              />
            </div>
          </div>
        </section>

        <section className="tour-section" id="ukazky" aria-label="Ukázky z appky">
          {productTourGroups.map((group) => (
            <article className="tour-group" key={group.id}>
              <div className="tour-copy">
                <p className="eyebrow">{group.kicker}</p>
                <h3>{group.title}</h3>
                <p>{group.text}</p>
              </div>
              <div className="tour-rail" aria-label={group.title}>
                {group.shots.map((shot) => (
                  <PhoneShot key={shot.id} shot={shot} className="tour-phone" />
                ))}
              </div>
            </article>
          ))}
        </section>

        <section className="vltava-section" id="vltava">
          <div className="section-heading">
            <p className="eyebrow">první rok</p>
            <h2>Začínáme jen na Vltavě.</h2>
            <p>
              Příští sezonu se podle zájmu rozšíříme i na ostatní řeky.
            </p>
            <p className="partner-note">
              <a href="mailto:info@na-vodu.cz?subject=Partnerstvi%20Na%20Vodu">
                Přejete si stát se partnerem? Napište nám.
              </a>
            </p>
          </div>
        </section>

        <section className="partners-section" id="partneri">
          <div className="section-heading partners-heading">
            <h2>Partneři aplikace</h2>
          </div>
          <div className="partner-groups">
            {appPartnerGroups.map((group) => (
              <section className="partner-group" key={group.id}>
                <h3>{group.label}</h3>
                <div className="partner-grid" aria-label={group.label}>
                  {group.partners.map((partner) => (
                    <a
                      aria-label={partner.name}
                      className={[
                        'partner-card',
                        partner.logoTone === 'dark' ? 'partner-card-dark' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      href={partner.href}
                      key={partner.id}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {partner.logoSrc ? (
                        <img
                          alt=""
                          aria-hidden="true"
                          className="partner-logo-image"
                          src={partner.logoSrc}
                        />
                      ) : (
                        <span className="partner-logo" aria-hidden="true">
                          {partner.logoLabel}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>

        <section className="start-section" id="start">
          <div className="start-brand">
            <img src="/assets/logo-current-n-stacked.svg" alt="" aria-hidden="true" />
            <div>
              <p className="eyebrow">ke stažení</p>
              <h2>Stáhnout appku Na Vodu.</h2>
            </div>
          </div>
          <div className="store-list">
            <CleanStoreLink platform="apple" />
            <CleanStoreLink platform="google" />
            <a className="contact-link" href="mailto:info@na-vodu.cz">
              info@na-vodu.cz
            </a>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-main">
          <a className="footer-brand" href="#uvod" aria-label="Na Vodu domů">
            <img src="/assets/logo-current-n.svg" alt="" />
            <span>
              <strong>Na Vodu</strong>
              <small>Vodácká aplikace</small>
            </span>
          </a>

          <div className="footer-columns">
            <div>
              <p>Navigace</p>
              <a href="#appka">Funkce</a>
              <a href="#ukazky">Ukázky</a>
              <a href="#vltava">Vltava</a>
              <a href="#partneri">Partneři</a>
              <a href="#start">Stažení</a>
            </div>
            <div>
              <p>Kontakt</p>
              <a href="mailto:info@na-vodu.cz">info@na-vodu.cz</a>
              <SocialLinks variant="footer" />
            </div>
            <div>
              <p>Právní</p>
              <a href="/legal/privacy/">Soukromí</a>
              <a href="/legal/terms/">Podmínky</a>
              <a href="/legal/safety/">Bezpečnost</a>
              <a href="/legal/community/">Komunita</a>
              <a href="/legal/cookies/">Cookies</a>
              <a href="/account/delete/">Smazání účtu</a>
            </div>
            <div>
              <p>Autor</p>
              <a href="https://martinandel.cz">Martin Anděl</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <span>© 2026 Na Vodu</span>
          <span>První sezona začíná na Vltavě.</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
